/**
 * LMS Course Editor (TRUS-225) — author modules & their components for one
 * course.
 *
 * A module is a sequence of components (stored as lms_lessons rows): pre-read
 * text, one or more slide decks (PPT/PDF), videos, embedded forms, quizzes and
 * surveys — each tracked for completion individually. "Add component" opens a
 * type picker per module.
 *
 * Every component can carry an optional Spanish version (translations.es);
 * the editor's English/Español toggle switches which variant is being edited,
 * and learners pick their language in the course player. Quiz/survey grading
 * always runs against the English structure, so the Spanish variant starts as
 * a copy of the English questions.
 */
import { useState, useEffect, useCallback, type ChangeEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Loader2,
  BookOpen,
  Video,
  FileText,
  Eye,
  EyeOff,
  ExternalLink,
  FolderPlus,
  ListChecks,
  CheckCircle,
  Circle,
  Presentation,
  Upload,
  X,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import WorkforceLayout from '../layout';
import { navigateToHubTab } from '../hubs/hubNavigation';
import { RichTextEditor } from '@/src/platform/modules/web-presence/components/RichTextEditor/RichTextEditor';
import { SLIDES_PROVIDERS } from './slidesEmbed';

type ContentType = 'text' | 'video' | 'form_embed' | 'quiz' | 'survey' | 'slides' | 'platform_survey';

interface QuizQuestion {
  prompt: string;
  options: string[];
  correct?: number; // index of correct option (quiz only)
  type?: 'choice' | 'text'; // 'text' = free-text answer (ungraded, analyzed in Insights)
}

interface LessonTranslation {
  title?: string;
  content?: Record<string, any>;
}

interface Lesson {
  id: number;
  title: string;
  content_type: ContentType;
  content: Record<string, any>;
  sort_order: number;
  translations?: Record<string, LessonTranslation>;
}
interface Module {
  id: number;
  title: string;
  description: string | null;
  sort_order: number;
  lessons: Lesson[];
}
interface CourseDetail {
  id: number;
  title: string;
  description: string | null;
  is_published: boolean;
  audience?: string;
  modules: Module[];
}

const activeCboId = () => localStorage.getItem('active_cbo_id') || '';
const api = (p: string) => getWeaveinApiUrl(p);
const jsonHeaders = { 'Content-Type': 'application/json' };

function lessonTypeIcon(t: ContentType) {
  if (t === 'video') return <Video className="w-3.5 h-3.5 shrink-0" />;
  if (t === 'form_embed') return <FileText className="w-3.5 h-3.5 shrink-0" />;
  if (t === 'quiz' || t === 'survey' || t === 'platform_survey') return <ListChecks className="w-3.5 h-3.5 shrink-0" />;
  if (t === 'slides') return <Presentation className="w-3.5 h-3.5 shrink-0" />;
  return <BookOpen className="w-3.5 h-3.5 shrink-0" />;
}

// The component types a module can contain. A module can hold any number of
// these — e.g. a pre-read, two slide decks (tracked separately), a video and
// a closing quiz.
const COMPONENT_TYPES: { type: ContentType; label: string; hint: string }[] = [
  { type: 'text', label: 'Pre-read (text)', hint: 'Written content, HTML supported' },
  { type: 'slides', label: 'Slides (PPT / PDF)', hint: 'One deck per component — add several for multiple files' },
  { type: 'video', label: 'Video', hint: 'YouTube link or an uploaded video file' },
  { type: 'form_embed', label: 'Form', hint: 'Embed an external form' },
  { type: 'quiz', label: 'Quiz', hint: 'Graded questions with a passing score' },
  { type: 'platform_survey', label: 'Survey', hint: 'A survey from Communications — tracked in survey analytics' },
];

export default function CourseEditorPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const cbo = activeCboId();

  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeLesson, setActiveLesson] = useState<Lesson | null>(null);
  const [addingModule, setAddingModule] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [addMenuFor, setAddMenuFor] = useState<number | null>(null); // module id with the "Add component" menu open
  const [editLang, setEditLang] = useState<'en' | 'es'>('en');
  const [fullscreenEditor, setFullscreenEditor] = useState(false);
  const [savingLesson, setSavingLesson] = useState(false);
  const [uploadingSlides, setUploadingSlides] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const load = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(api(`/api/lms/admin/courses/${courseId}?cbo_id=${cbo}`));
      if (!res.ok) throw new Error(res.status === 403 ? 'Admin access required.' : 'Course not found');
      const data: CourseDetail = await res.json();
      setCourse(data);
      // keep the active lesson selection in sync after reloads
      setActiveLesson((prev) => {
        if (!prev) return prev;
        for (const m of data.modules) {
          const found = m.lessons.find((l) => l.id === prev.id);
          if (found) return found;
        }
        return null;
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [courseId, cbo]);

  useEffect(() => {
    load();
  }, [load]);

  // always come back to English when switching between components
  useEffect(() => {
    setEditLang('en');
  }, [activeLesson?.id]);

  // ---- course-level ops ----
  const saveCourseMeta = async (patch: Partial<Pick<CourseDetail, 'title' | 'description' | 'audience'>>) => {
    if (!course) return;
    await authFetch(api(`/api/lms/admin/courses/${course.id}?cbo_id=${cbo}`), {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify(patch),
    });
  };

  const togglePublish = async () => {
    if (!course) return;
    const res = await authFetch(
      api(`/api/lms/admin/courses/${course.id}/publish?published=${!course.is_published}`),
      { method: 'PATCH' }
    );
    if (res.ok) setCourse({ ...course, is_published: !course.is_published });
  };

  // ---- module ops ----
  const addModule = async () => {
    if (!course || !newModuleTitle.trim()) return;
    setError('');
    const res = await authFetch(api(`/api/lms/admin/courses/${course.id}/modules`), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ title: newModuleTitle.trim(), sort_order: course.modules.length }),
    });
    if (res.ok) {
      setNewModuleTitle('');
      setAddingModule(false);
      await load();
    } else {
      const err = await res.json().catch(() => ({} as any));
      setError(typeof err.detail === 'string' ? err.detail : `Could not add the module (${res.status})`);
    }
  };

  const deleteModule = async (moduleId: number) => {
    if (!confirm('Delete this module and all its lessons?')) return;
    const res = await authFetch(api(`/api/lms/admin/modules/${moduleId}?cbo_id=${cbo}`), { method: 'DELETE' });
    if (res.ok) {
      if (activeLesson && course?.modules.find((m) => m.id === moduleId)?.lessons.some((l) => l.id === activeLesson.id)) {
        setActiveLesson(null);
      }
      await load();
    }
  };

  // ---- component (lesson) ops ----
  const addLesson = async (module: Module, type: ContentType, label: string) => {
    setAddMenuFor(null);
    setError('');
    const res = await authFetch(api(`/api/lms/admin/modules/${module.id}/lessons`), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ title: label, content_type: type, content: {}, sort_order: module.lessons.length }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({} as any));
      setError(typeof err.detail === 'string' ? err.detail : `Could not add the component (${res.status})`);
      return;
    }
    const created = await res.json();
    await load();
    setEditLang('en');
    setActiveLesson({
      id: created.id,
      title: label,
      content_type: type,
      content: {},
      sort_order: module.lessons.length,
      translations: {},
    });
  };

  const saveLesson = async () => {
    if (!activeLesson) return;
    setSavingLesson(true);
    try {
      const res = await authFetch(api(`/api/lms/admin/lessons/${activeLesson.id}?cbo_id=${cbo}`), {
        method: 'PATCH',
        headers: jsonHeaders,
        body: JSON.stringify({
          title: activeLesson.title,
          content_type: activeLesson.content_type,
          content: activeLesson.content,
          translations: activeLesson.translations || {},
        }),
      });
      if (!res.ok) throw new Error('Could not save lesson');
      await load();
    } catch (err: any) {
      setError(err.message || 'Could not save lesson');
    } finally {
      setSavingLesson(false);
    }
  };

  const deleteLesson = async (lessonId: number) => {
    if (!confirm('Delete this component?')) return;
    const res = await authFetch(api(`/api/lms/admin/lessons/${lessonId}?cbo_id=${cbo}`), { method: 'DELETE' });
    if (res.ok) {
      if (activeLesson?.id === lessonId) setActiveLesson(null);
      await load();
    }
  };

  const setLessonField = (patch: Partial<Lesson>) =>
    setActiveLesson((prev) => (prev ? { ...prev, ...patch } : prev));

  // ---- language-aware content access -------------------------------------
  // English edits the base title/content; Español edits translations.es.
  const editedTitle =
    editLang === 'en' ? activeLesson?.title ?? '' : activeLesson?.translations?.es?.title ?? '';
  const editedContent: Record<string, any> =
    editLang === 'en' ? activeLesson?.content ?? {} : activeLesson?.translations?.es?.content ?? {};

  const setTitleForLang = (value: string) =>
    setActiveLesson((prev) => {
      if (!prev) return prev;
      if (editLang === 'en') return { ...prev, title: value };
      const es = prev.translations?.es || {};
      return { ...prev, translations: { ...prev.translations, es: { ...es, title: value } } };
    });

  const mergeContent = (patch: Record<string, any>) =>
    setActiveLesson((prev) => {
      if (!prev) return prev;
      if (editLang === 'en') return { ...prev, content: { ...prev.content, ...patch } };
      const es = prev.translations?.es || {};
      return {
        ...prev,
        translations: { ...prev.translations, es: { ...es, content: { ...(es.content || {}), ...patch } } },
      };
    });

  const setContentField = (key: string, value: any) => mergeContent({ [key]: value });

  // ---- asset upload (PPTX/PDF for slides, video files) --------------------------
  // Uploads the picked file to storage and folds the returned URL into the lesson
  // content exactly like a pasted URL would be. The API returns a relative path
  // (/api/lms/assets/{token}); we make it absolute so the Office viewer / <video>
  // can load it. FormData is sent WITHOUT a manual Content-Type so the browser can
  // set the multipart boundary; authFetch still attaches Authorization + X-CBO-ID.
  const uploadAsset = async (file: File, kind: 'slides' | 'video') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    const res = await authFetch(api('/api/lms/admin/assets'), { method: 'POST', body: fd });
    if (!res.ok) {
      let detail = '';
      try {
        detail = (await res.json())?.detail || '';
      } catch {
        /* ignore */
      }
      throw new Error(detail || `Upload failed (${res.status})`);
    }
    return res.json() as Promise<{ url: string; filename?: string; kind: string; pdf_url?: string; converted?: boolean }>;
  };

  const handleSlidesUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploadError('');
    setUploadingSlides(true);
    try {
      const data = await uploadAsset(file, 'slides');
      // If the backend server-side-converted the deck to PDF (LibreOffice), render
      // that PDF directly (browsers display PDF inline in an iframe). Otherwise fall
      // back to the original file via Microsoft's Office web viewer.
      const content = data.pdf_url
        ? { provider: 'pdf', embed_url: api(data.pdf_url) }
        : { provider: 'office', embed_url: api(data.url) };
      mergeContent({ ...content, upload_filename: data.filename || file.name });
    } catch (err: any) {
      setUploadError(err?.message || 'Upload failed');
    } finally {
      setUploadingSlides(false);
    }
  };

  const handleVideoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploadError('');
    setUploadingVideo(true);
    try {
      const data = await uploadAsset(file, 'video');
      mergeContent({ url: api(data.url), upload_filename: data.filename || file.name });
    } catch (err: any) {
      setUploadError(err?.message || 'Upload failed');
    } finally {
      setUploadingVideo(false);
    }
  };

  // ---- quiz/survey question-builder helpers (operate on the edited
  //      language's content.questions) ----
  const getQuestions = (): QuizQuestion[] => (editedContent?.questions as QuizQuestion[]) || [];
  const setQuestions = (qs: QuizQuestion[]) => setContentField('questions', qs);
  // Spanish quiz/survey must mirror the English question/option structure
  // (grading runs against English) — start the translation from a copy.
  const copyQuestionsFromEnglish = () =>
    setQuestions(JSON.parse(JSON.stringify(activeLesson?.content?.questions || [])));
  const addQuestion = () =>
    setQuestions([...getQuestions(), { prompt: '', options: ['', ''], correct: undefined }]);
  const updateQuestion = (qi: number, patch: Partial<QuizQuestion>) =>
    setQuestions(getQuestions().map((q, i) => (i === qi ? { ...q, ...patch } : q)));
  const removeQuestion = (qi: number) => setQuestions(getQuestions().filter((_, i) => i !== qi));
  const setOption = (qi: number, oi: number, val: string) =>
    updateQuestion(qi, { options: getQuestions()[qi].options.map((o, i) => (i === oi ? val : o)) });
  const addOption = (qi: number) => updateQuestion(qi, { options: [...getQuestions()[qi].options, ''] });
  const removeOption = (qi: number, oi: number) =>
    updateQuestion(qi, { options: getQuestions()[qi].options.filter((_, i) => i !== oi) });

  if (loading) {
    return (
      <WorkforceLayout>
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      </WorkforceLayout>
    );
  }
  if (!course) {
    return (
      <WorkforceLayout>
        <div className="text-center py-24 text-gray-500">
          <p className="mb-4">{error || 'Course not found.'}</p>
          <Button variant="outline" onClick={() => navigateToHubTab(navigate, 'workforce-learning-hub', 'courses')}>
            Back to Learning
          </Button>
        </div>
      </WorkforceLayout>
    );
  }

  return (
    <WorkforceLayout>
      <div className="max-w-6xl mx-auto p-4">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigateToHubTab(navigate, 'workforce-learning-hub', 'courses')}
              className="gap-1 shrink-0"
            >
              <ArrowLeft className="w-4 h-4" /> Learning
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/workforce/courses/${course.id}/insights`}>
              <Button variant="outline" size="sm" className="gap-1">
                <ListChecks className="w-3.5 h-3.5" /> Insights
              </Button>
            </Link>
            <Link to={`/member/courses/${course.id}`} target="_blank">
              <Button variant="outline" size="sm" className="gap-1">
                <ExternalLink className="w-3.5 h-3.5" /> Preview
              </Button>
            </Link>
            <Button size="sm" variant={course.is_published ? 'outline' : 'default'} className="gap-1" onClick={togglePublish}>
              {course.is_published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {course.is_published ? 'Unpublish' : 'Publish'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Course meta */}
        <div className="mb-6 flex items-center gap-2">
          {course.is_published ? (
            <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 shrink-0">Published</Badge>
          ) : (
            <Badge variant="secondary" className="shrink-0">Draft</Badge>
          )}
          <Input
            className="text-lg font-semibold"
            value={course.title}
            onChange={(e) => setCourse({ ...course, title: e.target.value })}
            onBlur={(e) => saveCourseMeta({ title: e.target.value })}
          />
          <div className="shrink-0 flex items-center gap-1.5">
            <label className="text-xs text-gray-500">Audience</label>
            <select
              className="text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 dark:border-gray-700"
              value={course.audience || 'both'}
              onChange={(e) => {
                setCourse({ ...course, audience: e.target.value });
                saveCourseMeta({ audience: e.target.value } as any);
              }}
            >
              <option value="both">Staff &amp; Members</option>
              <option value="staff">Staff only</option>
              <option value="member">Members only</option>
            </select>
          </div>
        </div>
        <Textarea
          className="mb-6"
          placeholder="Course description"
          value={course.description || ''}
          rows={2}
          onChange={(e) => setCourse({ ...course, description: e.target.value })}
          onBlur={(e) => saveCourseMeta({ description: e.target.value })}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          {/* Sidebar: modules + lessons */}
          <aside className="space-y-4">
            {course.modules.map((mod) => (
              <div key={mod.id} className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <input
                    className="bg-transparent font-semibold text-sm text-gray-800 dark:text-gray-100 w-full focus:outline-none"
                    defaultValue={mod.title}
                    onBlur={(e) => {
                      if (e.target.value !== mod.title)
                        authFetch(api(`/api/lms/admin/modules/${mod.id}?cbo_id=${cbo}`), {
                          method: 'PATCH',
                          headers: jsonHeaders,
                          body: JSON.stringify({ title: e.target.value }),
                        }).then(load);
                    }}
                  />
                  <button onClick={() => deleteModule(mod.id)} className="text-gray-400 hover:text-red-600 shrink-0" title="Delete module">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <ul className="space-y-0.5">
                  {mod.lessons.map((lesson) => (
                    <li key={lesson.id}>
                      <button
                        onClick={() => setActiveLesson(lesson)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-sm transition-colors ${
                          activeLesson?.id === lesson.id
                            ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        {lessonTypeIcon(lesson.content_type)}
                        <span className="truncate">{lesson.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
                {addMenuFor === mod.id ? (
                  <div className="mt-1 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 overflow-hidden">
                    {COMPONENT_TYPES.map((ct) => (
                      <button
                        key={ct.type}
                        onClick={() => addLesson(mod, ct.type, ct.label)}
                        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-teal-50 dark:hover:bg-teal-900/20"
                      >
                        <span className="mt-0.5 text-teal-600 dark:text-teal-400">{lessonTypeIcon(ct.type)}</span>
                        <span>
                          <span className="block text-sm text-gray-800 dark:text-gray-100">{ct.label}</span>
                          <span className="block text-[11px] text-gray-400">{ct.hint}</span>
                        </span>
                      </button>
                    ))}
                    <button
                      onClick={() => setAddMenuFor(null)}
                      className="w-full px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 border-t dark:border-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 gap-1 text-xs text-teal-600 dark:text-teal-400"
                    onClick={() => setAddMenuFor(mod.id)}
                  >
                    <Plus className="w-3 h-3" /> Add component
                  </Button>
                )}
              </div>
            ))}

            {addingModule ? (
              <div className="border rounded-lg p-3 space-y-2">
                <Input placeholder="Module title" value={newModuleTitle} onChange={(e) => setNewModuleTitle(e.target.value)} autoFocus />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setAddingModule(false); setNewModuleTitle(''); }}>Cancel</Button>
                  <Button size="sm" onClick={addModule} disabled={!newModuleTitle.trim()}>Add</Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" className="w-full gap-2" onClick={() => setAddingModule(true)}>
                <FolderPlus className="w-4 h-4" /> Add module
              </Button>
            )}
          </aside>

          {/* Lesson editor */}
          <main>
            {!activeLesson ? (
              <div className="text-center py-24 text-gray-400 text-sm border rounded-lg">
                Select a component to edit, or add a module to get started.
              </div>
            ) : (
              <div className="border rounded-lg p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Edit component</h3>
                  <div className="flex items-center gap-3">
                    {/* Language of the variant being edited */}
                    <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
                      {(['en', 'es'] as const).map((l) => (
                        <button
                          key={l}
                          onClick={() => setEditLang(l)}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                            editLang === l
                              ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                          }`}
                        >
                          {l === 'en' ? 'English' : 'Español'}
                        </button>
                      ))}
                    </div>
                    <button onClick={() => deleteLesson(activeLesson.id)} className="text-gray-400 hover:text-red-600" title="Delete component">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {editLang === 'es' && (
                  <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                    Optional Spanish version. Anything left blank falls back to the English content for
                    learners who choose Español.
                  </p>
                )}

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    Title{editLang === 'es' ? ' (Español)' : ''}
                  </label>
                  <Input
                    value={editedTitle}
                    placeholder={editLang === 'es' ? activeLesson.title : undefined}
                    onChange={(e) => setTitleForLang(e.target.value)}
                  />
                </div>

                {editLang === 'en' && (
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Component type</label>
                    <div className="flex gap-2">
                      {(['text', 'video', 'slides', 'form_embed', 'quiz', 'platform_survey'] as ContentType[]).map((t) => (
                        <button
                          key={t}
                          onClick={() => setLessonField({ content_type: t })}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm capitalize transition-colors ${
                            activeLesson.content_type === t
                              ? 'border-teal-400 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-200'
                              : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          {lessonTypeIcon(t)} {t.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-type content editor */}
                {activeLesson.content_type === 'text' && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-gray-500">Content</label>
                      <button
                        type="button"
                        onClick={() => setFullscreenEditor(true)}
                        className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-700"
                      >
                        <Maximize2 className="w-3.5 h-3.5" /> Full screen
                      </button>
                    </div>
                    <RichTextEditor
                      key={`${activeLesson.id}-${editLang}`}
                      content={editedContent.html || editedContent.text || ''}
                      onChange={(html) => setContentField('html', html)}
                      minHeight="260px"
                      placeholder="Write the lesson content…"
                    />
                  </div>
                )}
                {activeLesson.content_type === 'platform_survey' && (
                  <InlineSurveyBuilder
                    cbo={cbo}
                    surveyId={editedContent.survey_id || ''}
                    defaultName={`${course.title} — ${activeLesson.title}`}
                    onLinked={(id, name) => {
                      setContentField('survey_id', id);
                      setContentField('survey_name', name);
                    }}
                  />
                )}
                {activeLesson.content_type === 'video' && (
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Video URL</label>
                    <Input
                      placeholder="https://www.youtube.com/watch?v=…"
                      value={editedContent.url || ''}
                      onChange={(e) => setContentField('url', e.target.value)}
                    />
                    <p className="text-xs text-gray-400 mt-1">Standard watch or youtu.be links are converted to an embed automatically.</p>
                    <div className="mt-2 flex items-center gap-2">
                      <label
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                          uploadingVideo ? 'opacity-60 pointer-events-none' : 'cursor-pointer'
                        }`}
                      >
                        {uploadingVideo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Upload video
                        <input
                          type="file"
                          accept="video/*"
                          className="hidden"
                          disabled={uploadingVideo}
                          onChange={handleVideoUpload}
                        />
                      </label>
                      {editedContent.upload_filename && (
                        <span className="text-xs text-gray-500 truncate max-w-[240px]">{editedContent.upload_filename}</span>
                      )}
                    </div>
                    {uploadError && <p className="text-xs text-red-600 mt-1">{uploadError}</p>}
                  </div>
                )}
                {activeLesson.content_type === 'slides' && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Provider</label>
                      <select
                        className="text-sm border rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 dark:border-gray-700"
                        value={editedContent.provider || 'google_slides'}
                        onChange={(e) => setContentField('provider', e.target.value)}
                      >
                        {SLIDES_PROVIDERS.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">
                        {(editedContent.provider || 'google_slides') === 'canva'
                          ? 'Canva share link'
                          : (editedContent.provider || 'google_slides') === 'google_slides'
                          ? 'Google Slides published embed URL'
                          : 'Public file URL (.pptx / .ppt / .pdf)'}
                      </label>
                      <Input
                        placeholder={
                          (editedContent.provider || 'google_slides') === 'canva'
                            ? 'https://www.canva.com/design/…/view'
                            : (editedContent.provider || 'google_slides') === 'google_slides'
                            ? 'https://docs.google.com/presentation/d/e/…/embed'
                            : 'https://example.com/deck.pptx'
                        }
                        value={editedContent.embed_url || ''}
                        onChange={(e) => setContentField('embed_url', e.target.value)}
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {(editedContent.provider || 'google_slides') === 'canva'
                          ? 'In Canva: Share → More → Embed (or just paste the design’s share link). It renders inline; viewers need link access.'
                          : (editedContent.provider || 'google_slides') === 'google_slides'
                          ? 'In Google Slides: File → Share → Publish to web → Embed, then paste the iframe src (…/embed).'
                          : 'Paste a public URL to a PowerPoint or PDF, or upload a file below; it renders through the Microsoft Office web viewer.'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Or upload a file</label>
                      <div className="flex items-center gap-2">
                        <label
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                            uploadingSlides ? 'opacity-60 pointer-events-none' : 'cursor-pointer'
                          }`}
                        >
                          {uploadingSlides ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          Upload PPTX/PDF
                          <input
                            type="file"
                            accept=".pptx,.ppt,.pdf"
                            className="hidden"
                            disabled={uploadingSlides}
                            onChange={handleSlidesUpload}
                          />
                        </label>
                        {editedContent.upload_filename && (
                          <span className="text-xs text-gray-500 truncate max-w-[240px]">{editedContent.upload_filename}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Uploading sets the provider to the Office viewer automatically.</p>
                      {uploadError && <p className="text-xs text-red-600 mt-1">{uploadError}</p>}
                    </div>
                  </div>
                )}
                {activeLesson.content_type === 'form_embed' && (
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Form embed URL</label>
                    <Input
                      placeholder="https://…"
                      value={editedContent.embed_url || editedContent.url || ''}
                      onChange={(e) => setContentField('embed_url', e.target.value)}
                    />
                    <p className="text-xs text-gray-400 mt-1">A public form / survey URL to embed in an iframe.</p>
                  </div>
                )}
                {(activeLesson.content_type === 'quiz' || activeLesson.content_type === 'survey') && (
                  <div className="space-y-4">
                    {activeLesson.content_type === 'quiz' && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500">Passing score (%)</label>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          className="w-24"
                          value={editedContent.passing_score ?? 70}
                          onChange={(e) => setContentField('passing_score', Number(e.target.value))}
                        />
                        <span className="text-xs text-gray-400">Learners must score at least this to complete.</span>
                      </div>
                    )}
                    {activeLesson.content_type === 'survey' && (
                      <p className="text-xs text-gray-500">
                        Survey mode: responses are captured, no right/wrong answers — completes on submit.
                      </p>
                    )}

                    {editLang === 'es' &&
                      getQuestions().length === 0 &&
                      ((activeLesson.content?.questions as any[]) || []).length > 0 && (
                        <div className="flex items-center justify-between gap-3 rounded-lg border border-teal-200 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-800 px-3 py-2">
                          <p className="text-xs text-teal-700 dark:text-teal-300">
                            Start the Spanish version from the English questions, then translate each
                            prompt and option in place (keep the same order — grading follows the
                            English structure).
                          </p>
                          <Button variant="outline" size="sm" className="shrink-0" onClick={copyQuestionsFromEnglish}>
                            Copy English questions
                          </Button>
                        </div>
                      )}

                    {getQuestions().map((q, qi) => (
                      <div key={qi} className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50 space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-semibold text-gray-500 mt-2 shrink-0">Q{qi + 1}</span>
                          <Textarea
                            rows={2}
                            placeholder="Question prompt"
                            value={q.prompt}
                            onChange={(e) => updateQuestion(qi, { prompt: e.target.value })}
                          />
                          <button onClick={() => removeQuestion(qi)} className="text-gray-400 hover:text-red-600 mt-2 shrink-0" title="Remove question">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <label className="flex items-center gap-2 pl-6 text-xs text-gray-500">
                          <input
                            type="checkbox"
                            checked={q.type === 'text' || (q.options || []).length === 0}
                            onChange={(e) =>
                              updateQuestion(
                                qi,
                                e.target.checked
                                  ? { type: 'text', options: [], correct: undefined }
                                  : { type: 'choice', options: ['', ''] },
                              )
                            }
                            className="rounded"
                          />
                          Free-text answer (written response — tracked &amp; analyzed in Insights)
                        </label>
                        {q.type !== 'text' && (q.options || []).length > 0 && (
                        <div className="space-y-1.5 pl-6">
                          {q.options.map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              {activeLesson.content_type === 'quiz' && (
                                <button
                                  type="button"
                                  onClick={() => updateQuestion(qi, { correct: oi })}
                                  title="Mark as correct answer"
                                  className="shrink-0"
                                >
                                  {q.correct === oi ? (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <Circle className="w-4 h-4 text-gray-300" />
                                  )}
                                </button>
                              )}
                              <Input
                                placeholder={`Option ${oi + 1}`}
                                value={opt}
                                onChange={(e) => setOption(qi, oi, e.target.value)}
                              />
                              {q.options.length > 2 && (
                                <button onClick={() => removeOption(qi, oi)} className="text-gray-400 hover:text-red-600 shrink-0" title="Remove option">
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                          <Button variant="ghost" size="sm" className="gap-1 text-xs text-teal-600" onClick={() => addOption(qi)}>
                            <Plus className="w-3 h-3" /> Add option
                          </Button>
                        </div>
                        )}
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="gap-1" onClick={addQuestion}>
                      <Plus className="w-4 h-4" /> Add question
                    </Button>
                    {activeLesson.content_type === 'quiz' &&
                      getQuestions().some((q) => q.type !== 'text' && (q.options || []).length > 0 && q.correct === undefined) && (
                      <p className="text-xs text-amber-600">Tip: mark a correct answer (green check) for each question so it can be graded.</p>
                    )}
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={saveLesson} disabled={savingLesson || uploadingSlides || uploadingVideo} className="gap-1.5">
                    {savingLesson ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save component
                  </Button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Full-screen rich-text editing. Edits the active lesson's content for the
          current language; "Done" saves and returns to the component editor. */}
      {fullscreenEditor && activeLesson && activeLesson.content_type === 'text' && (
        <div className="fixed inset-0 z-[60] bg-white dark:bg-gray-900 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700">
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Editing {editLang === 'es' ? '(Español)' : '(English)'}</p>
              <h3 className="text-sm font-semibold text-ink truncate">{activeLesson.title}</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  await saveLesson();
                  setFullscreenEditor(false);
                }}
                disabled={savingLesson}
                className="gap-1.5"
              >
                {savingLesson ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save &amp; close
              </Button>
              <button
                onClick={() => setFullscreenEditor(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
                title="Exit full screen (keep editing)"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-4xl mx-auto">
              <RichTextEditor
                key={`fs-${activeLesson.id}-${editLang}`}
                content={editedContent.html || editedContent.text || ''}
                onChange={(html) => setContentField('html', html)}
                minHeight="70vh"
                placeholder="Write the lesson content…"
              />
            </div>
          </div>
        </div>
      )}
    </WorkforceLayout>
  );
}


// ---------------------------------------------------------------------------
// Inline survey builder for a platform_survey lesson.
//
// Authors the class survey WITHOUT leaving the course editor, on the built-in
// Communications survey infra: create/edit questions here, saved via the
// surveys API (POST /api/surveys, PUT /api/surveys/{id}, kept 'active' so
// learners can respond). Also supports linking an existing survey to reuse one
// across modules. Responses show up in the course Insights view (not
// Communications) via the shared response-insights pipeline.
// ---------------------------------------------------------------------------
type BuilderQ = {
  question_text: string;
  question_text_es?: string;
  question_type: 'text' | 'multiple_choice' | 'rating' | 'yes_no';
  options?: string[];
  required?: boolean;
};

function InlineSurveyBuilder({
  cbo,
  surveyId,
  defaultName,
  onLinked,
}: {
  cbo: string;
  surveyId: string;
  defaultName: string;
  onLinked: (id: string, name: string) => void;
}) {
  const [questions, setQuestions] = useState<BuilderQ[]>([]);
  const [name, setName] = useState(defaultName);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [existing, setExisting] = useState<{ id: string; name: string; status?: string }[]>([]);

  // Load the linked survey's questions (edit mode) + the org's surveys (link mode).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const listRes = await authFetch(getWeaveinApiUrl('/api/surveys'));
        if (listRes.ok && !cancelled) {
          const data = await listRes.json();
          const items = Array.isArray(data) ? data : data.surveys || data.items || [];
          setExisting(items.map((s: any) => ({ id: String(s.id), name: s.name, status: s.status })));
        }
        if (surveyId) {
          const qRes = await authFetch(api(`/api/surveys/${surveyId}/questions`));
          if (qRes.ok && !cancelled) {
            const qs = await qRes.json();
            setQuestions(
              (Array.isArray(qs) ? qs : qs.questions || []).map((q: any) => ({
                question_text: q.question_text,
                question_text_es: q.translations?.es || '',
                question_type: ['text', 'multiple_choice', 'rating', 'yes_no'].includes(q.question_type)
                  ? q.question_type
                  : 'text',
                options: q.options || undefined,
                required: q.required ?? false,
              })),
            );
          }
        } else {
          setQuestions([{ question_text: '', question_type: 'text', required: false }]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
  }, [surveyId]);

  const setQ = (i: number, patch: Partial<BuilderQ>) =>
    setQuestions((qs) => qs.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const addQ = () => setQuestions((qs) => [...qs, { question_text: '', question_type: 'text', required: false }]);
  const removeQ = (i: number) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));

  const toPayloadQuestions = () =>
    questions
      .filter((q) => q.question_text.trim())
      .map((q, order) => ({
        question_text: q.question_text.trim(),
        question_type: q.question_type,
        options: q.question_type === 'multiple_choice' ? (q.options || []).filter((o) => o.trim()) : undefined,
        required: !!q.required,
        order,
        translations: q.question_text_es?.trim() ? { es: q.question_text_es.trim() } : {},
      }));

  const save = async () => {
    const payloadQs = toPayloadQuestions();
    if (payloadQs.length === 0) {
      setMsg('Add at least one question.');
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      if (surveyId) {
        const res = await authFetch(api(`/api/surveys/${surveyId}`), {
          method: 'PUT',
          jsonBody: { name, status: 'active', questions: payloadQs },
        });
        if (!res.ok) throw new Error('save failed');
        onLinked(surveyId, name);
        setMsg('Survey saved.');
      } else {
        // Create (draft), then activate so learners can respond.
        const res = await authFetch(api('/api/surveys'), {
          method: 'POST',
          jsonBody: {
            name,
            organization_id: Number(cbo),
            questions: payloadQs,
            response_channel: 'web',
            collect_anonymous: true,
            default_language: 'en',
            supported_languages: ['en', 'es'],
          },
        });
        if (!res.ok) throw new Error('create failed');
        const created = await res.json();
        const newId = String(created.id);
        await authFetch(api(`/api/surveys/${newId}`), { method: 'PUT', jsonBody: { status: 'active' } });
        onLinked(newId, name);
        setMsg('Survey created and linked.');
      }
    } catch {
      setMsg('Could not save the survey.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs text-gray-500">Class survey (built on Communications surveys)</label>
        {existing.length > 0 && (
          <select
            className="text-xs border rounded-md px-2 py-1 bg-white dark:bg-gray-800 dark:border-gray-700"
            value={surveyId}
            onChange={async (e) => {
              const s = existing.find((x) => x.id === e.target.value);
              onLinked(e.target.value, s?.name || name);
            }}
            title="Link an existing survey to reuse it"
          >
            <option value="">＋ New survey</option>
            {existing.map((s) => (
              <option key={s.id} value={s.id}>
                Link: {s.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Survey name" className="text-sm" />

      {loading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-2">
          {questions.map((q, i) => (
            <div key={i} className="border rounded-lg p-2.5 bg-gray-50 dark:bg-gray-800/50 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-xs font-semibold text-gray-500 mt-2 shrink-0">Q{i + 1}</span>
                <div className="flex-1 space-y-1.5">
                  <Input
                    value={q.question_text}
                    onChange={(e) => setQ(i, { question_text: e.target.value })}
                    placeholder="Question (English)"
                  />
                  <Input
                    value={q.question_text_es || ''}
                    onChange={(e) => setQ(i, { question_text_es: e.target.value })}
                    placeholder="Pregunta (Español) — optional"
                    className="text-sm"
                  />
                  <div className="flex items-center gap-2">
                    <select
                      className="text-xs border rounded-md px-2 py-1 bg-white dark:bg-gray-800 dark:border-gray-700"
                      value={q.question_type}
                      onChange={(e) => setQ(i, { question_type: e.target.value as BuilderQ['question_type'] })}
                    >
                      <option value="text">Free text</option>
                      <option value="multiple_choice">Multiple choice</option>
                      <option value="rating">Rating (1-5)</option>
                      <option value="yes_no">Yes / No</option>
                    </select>
                    <label className="flex items-center gap-1 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={!!q.required}
                        onChange={(e) => setQ(i, { required: e.target.checked })}
                      />
                      Required
                    </label>
                  </div>
                  {q.question_type === 'multiple_choice' && (
                    <Textarea
                      rows={2}
                      className="text-sm"
                      placeholder="One option per line"
                      value={(q.options || []).join('\n')}
                      onChange={(e) => setQ(i, { options: e.target.value.split('\n') })}
                    />
                  )}
                </div>
                <button onClick={() => removeQ(i)} className="text-gray-400 hover:text-red-600 mt-2 shrink-0" title="Remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" className="gap-1" onClick={addQ}>
            <Plus className="w-3.5 h-3.5" /> Add question
          </Button>
        </div>
      )}

      {msg && <p className="text-xs text-teal-700">{msg}</p>}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Responses appear in this course's Insights.</p>
        <Button size="sm" onClick={save} disabled={saving} className="gap-1.5">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {surveyId ? 'Save survey' : 'Create survey'}
        </Button>
      </div>
    </div>
  );
}
